import { DEFAULT_PAGE_SIZE, Dataset, DtFilter, DtSort } from '@/components/data-table/helpers';
import { PartnerUserModel } from '../models/partner-user.model';
import { JsonObject } from '../helpers';
import { CreateResponse, DeleteResponse, PatchResponse, passErrorResponse } from '.';
import { create, deleteApi, get, patch, post } from './sp-api.service';
import { PartnerModel } from '../models/partner.model';

const SAFE_LIMIT = 200;
const MAX_SEARCH_PAGES = 50;

type ListResponse = {
  count: number;
  data: JsonObject[];
};

export const getPartnerUsers = async (
  page = 0,
  pageSize = DEFAULT_PAGE_SIZE,
  dtSort?: DtSort,
  dtFilter?: DtFilter
): Promise<Dataset<PartnerUserModel>> => {
  const response = (await post('/user/partner-users/search', {
    page: 1,
    pageSize: SAFE_LIMIT,
    count: false,
    sort: [dtSort ? { [dtSort.columnName]: dtSort.direction } : { name: 'asc' }],
  })) as ListResponse;

  if (!response?.data) {
    return {
      page,
      pageSize,
      rows: [],
      total: 0,
    };
  }

  let rows: PartnerUserModel[] = response.data.map((data) => new PartnerUserModel(data));

  if (dtFilter) {
    const { values, keyword } = dtFilter;

    if (keyword) {
      const safeKeyword = String(keyword).toLowerCase();
      rows = rows.filter(
        ({ name, email }) =>
          name.toLowerCase().includes(safeKeyword) || email.toLowerCase().includes(safeKeyword)
      );
    }

    if (values?.length) {
      values.forEach(({ columnName, value }) => {
        switch (columnName) {
          case 'role':
            rows = rows.filter(({ role }) => role === value);
            break;
        }
      });
    }
  }

  const total = rows.length;

  // Paginate
  const startIndex = page * pageSize;
  rows = rows.slice(startIndex, startIndex + pageSize);

  return {
    page,
    pageSize,
    rows,
    total,
  };
};

const OWNER_CHECK_FAILED = 'Could not confirm this partner has another owner. Please try again.';

const partnerUsersPath = (partnerId?: string) =>
  partnerId ? `/user/partners/${partnerId}/partner-users` : '/user/partner-users';

export const getPartnerUserById = async (
  userId: string,
  partnerId?: string
): Promise<PartnerUserModel | null> => {
  const response = await get(`${partnerUsersPath(partnerId)}/${userId}`);
  return response ? new PartnerUserModel(response as JsonObject) : null;
};

export const patchPartnerUser = async (
  userId: string,
  data: JsonObject,
  partnerId?: string
): Promise<PatchResponse> => {
  // cannot change last owner role to non-owner
  if (data.role && data.role !== 'partner:owner') {
    const user = await getPartnerUserById(userId, partnerId);
    if (user?.role === 'partner:owner') {
      const lastOwner = await isLastOwner(partnerId).catch(() => null);
      if (lastOwner === null) {
        return { patched: false, error: OWNER_CHECK_FAILED };
      }
      if (lastOwner) {
        return { patched: false, error: 'You cannot change the role of the last owner' };
      }
    }
  }

  return patch(`${partnerUsersPath(partnerId)}/${userId}`, data);
};

export const createPartnerUser = async (user: PartnerUserModel): Promise<CreateResponse> => {
  const response = await create('/user/partner-users', user._props);

  if (!response.created) {
    return response;
  }

  response.model = new PartnerUserModel(response.data);

  return response;
};

// The search API caps a page at SAFE_LIMIT and cannot filter by role, so page through every user.
const searchAllPartnerUsers = async (partnerId?: string): Promise<PartnerUserModel[]> => {
  const users: PartnerUserModel[] = [];

  for (let page = 1; page <= MAX_SEARCH_PAGES; page++) {
    const response = (await post(
      `${partnerUsersPath(partnerId)}/search`,
      {
        page,
        pageSize: SAFE_LIMIT,
        count: false,
        sort: [{ name: 'asc' }],
      },
      passErrorResponse
    )) as ListResponse;

    const data = response?.data || [];
    users.push(...data.map((item) => new PartnerUserModel(item)));

    if (data.length < SAFE_LIMIT) {
      return users;
    }
  }

  throw new Error(`More than ${MAX_SEARCH_PAGES * SAFE_LIMIT} partner users`);
};

export const getPartnerMembers = async (partnerId: string): Promise<PartnerUserModel[]> =>
  searchAllPartnerUsers(partnerId);

export const createPartnerMember = async (
  partnerId: string,
  user: PartnerUserModel
): Promise<CreateResponse> => {
  const response = await create(
    partnerUsersPath(partnerId),
    user.propSlice(['name', 'email', 'role'])
  );

  if (!response.created) {
    return response;
  }

  response.model = new PartnerUserModel(response.data);

  return response;
};

export const deletePartnerUser = async (
  user: PartnerUserModel,
  partnerId?: string
): Promise<DeleteResponse> => {
  const { role, id: userId } = user;
  if (role === 'partner:owner') {
    // cannot delete last owner
    const lastOwner = await isLastOwner(partnerId).catch(() => null);
    if (lastOwner === null) {
      return { deleted: false, error: OWNER_CHECK_FAILED };
    }
    if (lastOwner) {
      return { deleted: false, error: 'You cannot delete the last owner' };
    }
  }
  return deleteApi(`${partnerUsersPath(partnerId)}/${userId}`);
};

export const isLastOwner = async (partnerId?: string): Promise<boolean> => {
  const ownerCount = await getOwnerCount(partnerId);
  return ownerCount < 2;
};

// @todo perf just get a count, don't return documents
export const getOwnerCount = async (partnerId?: string): Promise<number> => {
  const users = await searchAllPartnerUsers(partnerId);
  return users.filter(({ role }) => role === 'partner:owner').length;
};

export const isPartnerUserDomainInvalid = (
  partnerUser: PartnerUserModel,
  partner: PartnerModel | null
): boolean => {
  const { email, emailDomain } = partnerUser;
  if (!email || !emailDomain || !partner) {
    return false;
  }
  return !partner.domains.includes(emailDomain);
};
